import {Skeleton, type TextSkeletonProps} from '@dropbox/dig-components/skeleton';
import {Box, Split} from '@dropbox/dig-foundations';
import {t} from 'i18next';
import {useRef} from 'react';

import styles from './Apps.module.css';
import {useShelfBleed} from './useShelfBleed';

const TAB_WIDTHS = [110, 40, 60, 60, 56, 70, 190, 180, 120, 90, 150, 100, 130, 80] as const;
const NAME_WIDTHS: TextSkeletonProps['width'][] = [150, 110, 170, 130, 160, 120];
// Enough shelves to run off the page; each one fades a step further, like Table's skeleton rows.
const SHELF_COUNT = 6;
const SHELF_FADE_STEP = 0.15;
const TAB_FADE_STEP = 0.05;

const AppTileSkeleton = ({nameWidth}: {nameWidth: TextSkeletonProps['width']}) => (
  <div className={styles.appTile}>
    <div className={styles.appTileOverlay}>
      <Skeleton.Text size="large" width={nameWidth} />
      <Split alignY="center" gap="8">
        <Split.Item>
          <Skeleton.Avatar size="xsmall" />
        </Split.Item>
        <Split.Item>
          <Skeleton.Text size="small" width={90} />
        </Split.Item>
      </Split>
    </div>
  </div>
);

const AppShelfSkeleton = ({index}: {index: number}) => (
  <div className={styles.appShelf} style={{opacity: 1 - index * SHELF_FADE_STEP}}>
    <div className={styles.appShelfHeadingSkeleton}>
      <Skeleton.Box height={20} width={160} borderRadius="XSmall" />
    </div>
    <div className={styles.appShelfRail}>
      {NAME_WIDTHS.map((_, tileIndex) => (
        <AppTileSkeleton
          key={tileIndex}
          nameWidth={NAME_WIDTHS[(tileIndex + index * 2) % NAME_WIDTHS.length]}
        />
      ))}
    </div>
  </div>
);

/**
 * Placeholder in the shape of the loaded catalog: the org tab row over shelves that fade out
 * down the page.
 */
export const AppCatalogSkeleton = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  useShelfBleed(rootRef);

  return (
    <div ref={rootRef} aria-busy aria-label={t('apps_loading')}>
      <div className={styles.appOrgTabs}>
        {TAB_WIDTHS.map((width, index) => (
          <Box key={index} padding="12" style={{opacity: 1 - index * TAB_FADE_STEP}}>
            <Skeleton.Text size="small" width={width} />
          </Box>
        ))}
      </div>
      <div className={styles.appOrgShelves}>
        {Array.from({length: SHELF_COUNT}, (_, index) => (
          <AppShelfSkeleton key={index} index={index} />
        ))}
      </div>
    </div>
  );
};
